import type { IconWeight } from '@phosphor-icons/react';
import { Airplane, Bus, Train } from '@phosphor-icons/react';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { CarrierLogo, Icon } from '@eti/components';
import { Inline, Stack, vars } from '@etg/wings';
import type {
  BrandedFareInfo,
  IncludedCabinBaggage,
  IncludedCheckedBaggage,
} from '@eti/schema-types';
import {
  AIR_EQUIPMENT_CODE,
  BUS_EQUIPMENT_CODE,
  TRAIN_EQUIPMENT_CODE,
} from '../../constants/transportationMeansConstants';
import { useBrandedFareName } from '../../common/custom-hooks/useBrandedFareName';
import { boundMargins, IconWrapper } from './ItineraryComponents';
import ItineraryBaggageInfo from './ItineraryBaggageInfo';

const wrapperStyles = css`
  align-items: center;
  background-color: #fafafa;
  display: flex;
  padding: 16px 0;
  position: relative;

  &::before {
    background-color: ${vars.colors.inputs.border};
    ${boundMargins};
    top: 0;
    width: 1px;
  }

  &::after {
    background-color: ${vars.colors.inputs.border};
    bottom: 0;
    ${boundMargins};
    width: 1px;
  }
`;

const flightInfoWrapperStyles = css`
  padding-inline: 20px 16px;
  width: 100%;
`;

const durationStyles = css`
  display: flex;
  flex: 0 0 89px;
  justify-content: center;
`;

const dividerStyles = css`
  background-color: ${vars.colors.divider};
  height: 1px;
  margin-bottom: 6px;
  margin-top: 6px;
  width: 100%;
`;

const carrierLogoInfoStyles = css`
  height: 16px;
  width: 16px;
`;

const planeIconStyles = (weight: IconWeight) => css`
  ${weight === 'fill' && `transform: rotate(180deg)`};
`;

const getVehicleTypes = (size: number, weight: IconWeight) => ({
  [AIR_EQUIPMENT_CODE]: {
    component: () => (
      <Airplane
        className={planeIconStyles(weight)}
        data-testid="trip-segment-info-plane-icon"
        size={size}
        weight={weight}
      />
    ),
  },
  [BUS_EQUIPMENT_CODE]: {
    component: () => <Bus data-testid="trip-segment-info-bus-icon" size={size} weight={weight} />,
  },
  [TRAIN_EQUIPMENT_CODE]: {
    component: () => (
      <Train data-testid="trip-segment-info-train-icon" size={size} weight={weight} />
    ),
  },
});

const VehicleTypes = {
  AIR: 'AIR',
  TRN: 'TRN',
  BUS: 'BUS',
} as const;
type VehicleTypesType = (typeof VehicleTypes)[keyof typeof VehicleTypes];

interface Carrier {
  code: string;
  name: string;
}

export interface ItinerarySegmentInfoProps {
  brandedFareInformation: BrandedFareInfo;
  cabinClassName: string;
  duration: string;
  flightNumber: string;
  includedCabinBaggage: IncludedCabinBaggage;
  includedCheckedBaggage: IncludedCheckedBaggage;
  marketingCarrier: Carrier;
  numberOfTechnicalStops: number;
  operatingCarrier: Carrier;
  operatingInformation: string;
  shouldHideBaggageInfo: boolean;
  transportType: string;
  tripSegmentType: string;
}

const ItinerarySegmentInfo = ({
  brandedFareInformation,
  cabinClassName,
  duration,
  flightNumber,
  includedCabinBaggage,
  includedCheckedBaggage,
  marketingCarrier,
  numberOfTechnicalStops = 0,
  operatingCarrier,
  operatingInformation,
  shouldHideBaggageInfo = false,
  transportType,
  tripSegmentType,
}: ItinerarySegmentInfoProps) => {
  const { t } = useTranslation();
  const vehicleTypes = getVehicleTypes(18, 'fill');
  const translatedCabinClassName = t(`IbeClient.CabinClass.${cabinClassName}.Text`);
  const brandedFareName = useBrandedFareName({
    brandedFareInformation,
    marketingCarrier,
  });
  const travelType =
    vehicleTypes[tripSegmentType as VehicleTypesType] ?? vehicleTypes[AIR_EQUIPMENT_CODE];

  const operatedBy =
    operatingInformation ||
    (operatingCarrier.code !== marketingCarrier.code ? operatingCarrier.name : null);

  const transportTypes = getVehicleTypes(16, 'light');
  const transportationType =
    transportTypes[tripSegmentType as VehicleTypesType] ?? transportTypes[AIR_EQUIPMENT_CODE];

  return (
    <div className={wrapperStyles} data-testid="trip-segment-info">
      <div className={durationStyles} data-testid="trip-segment-info-duration">
        {duration}
      </div>
      <IconWrapper>
        <travelType.component />
      </IconWrapper>
      <Stack className={flightInfoWrapperStyles}>
        <>
          <Stack spacing={4}>
            <p data-testid="trip-segment-info-cabinclass">
              <strong>{translatedCabinClassName}</strong>
            </p>
            {Boolean(brandedFareName) && (
              <Inline alignY="center" spacing={8}>
                <Icon color={vars.colors.text.primary} id="circle" size="4px" />
                <p data-testid="trip-segment-info-branded-fare-name">{brandedFareName}</p>
              </Inline>
            )}
          </Stack>
          {Boolean(brandedFareName) && <div className={dividerStyles} />}
        </>
        {!shouldHideBaggageInfo && (
          <>
            <Stack spacing={8}>
              <ItineraryBaggageInfo
                includedCabinBaggage={includedCabinBaggage}
                includedCheckedBaggage={includedCheckedBaggage}
              />
            </Stack>
            <div className={dividerStyles} />
          </>
        )}
        <Stack spacing={8}>
          {numberOfTechnicalStops !== 0 && (
            <Inline alignY="center" noWrap spacing={8}>
              <Icon id="technical-stop" size="16px" />
              <p data-testid="trip-segment-info-technicalStop">
                {t('CartTripDetails.TechnicalStop')}
              </p>
            </Inline>
          )}
          <Inline alignY="center" noWrap spacing={8}>
            <CarrierLogo
              className={carrierLogoInfoStyles}
              data-testid="trip-segment-info-carrierLogo"
              id={marketingCarrier.code || ''}
              imageAlt={marketingCarrier.name}
            />
            <Inline alignY="center" data-testid="trip-segment-info-carrier" noWrap spacing={8}>
              {marketingCarrier.name}
              <Icon color={vars.colors.text.primary} id="circle" size="4px" /> {flightNumber}
            </Inline>
          </Inline>
          {operatedBy && (
            <Inline alignY="center" noWrap spacing={8}>
              <CarrierLogo
                className={carrierLogoInfoStyles}
                data-testid="trip-segment-info-operating-carrierLogo"
                id={operatingCarrier.code || ''}
                imageAlt={operatingCarrier.name}
              />
              <p data-testid="trip-segment-info-operating-carrier">
                {t('TripDetails.OperatingCarrier')} {operatedBy}
              </p>
            </Inline>
          )}
          {tripSegmentType !== TRAIN_EQUIPMENT_CODE && transportType && (
            <Inline alignY="center" noWrap spacing={8}>
              <transportationType.component />
              <p data-testid="trip-segment-info-transportType">{transportType}</p>
            </Inline>
          )}
        </Stack>
      </Stack>
    </div>
  );
};

export default ItinerarySegmentInfo;
